import type { JSX, ReactNode } from 'react';
import { cx } from './primitives';
import { TableBand, TablePager, type TablePagerLabels } from './table-chrome';
import { TableFrame } from './table-frame';
import type { TableState } from './table-state';

/**
 * A table, the band under it and the pager on that band, as ONE unit wired to
 * `useTable`.
 *
 * Outside fill mode the unit is a flow-root (`.cib-tableunit`) so a gapped
 * parent column cannot open between the rows and their numbers. In fill mode
 * it is the flex column (`.cib-fill`) that takes the rest of a
 * `.cib-page--fill` screen: the frame owns the only vertical scrollport, its
 * header pins, and the band is the last natural-height item under it - which
 * is what keeps the count and the pager at the bottom of the screen however
 * many rows there are. `FillPage` is the one way to make a screen root that
 * column.
 */
export interface TableUnitProps<Row, Key extends string> {
  readonly table: Pick<TableState<Row, Key>, 'slice' | 'setPage'>;
  /** Accessible name of the scroll region. Already translated. */
  readonly label: string;
  readonly labels: TablePagerLabels;
  /** Left of the band: what the table holds, e.g. "12 rows". */
  readonly caption?: ReactNode;
  readonly fill?: boolean;
  readonly compact?: boolean;
  readonly cards?: boolean;
  /** Follow the cursor at the end of the rows held (see `TablePager`). */
  readonly onMore?: () => void;
  readonly loadingMore?: boolean;
  /** `<thead>` and `<tbody>`, written by the screen. */
  readonly children: ReactNode;
}

export function TableUnit<Row, Key extends string>({
  table,
  label,
  labels,
  caption,
  fill = false,
  compact = false,
  cards = true,
  onMore,
  loadingMore,
  children,
}: TableUnitProps<Row, Key>): JSX.Element {
  return (
    <div className={fill ? 'cib-fill' : 'cib-tableunit'}>
      <TableFrame label={label} fill={fill} compact={compact} cards={cards}>
        {children}
      </TableFrame>
      <TableBand
        caption={caption}
        pager={
          <TablePager
            slice={table.slice}
            onPage={table.setPage}
            labels={labels}
            {...(onMore === undefined ? {} : { onMore })}
            {...(loadingMore === undefined ? {} : { loadingMore })}
          />
        }
      />
    </div>
  );
}

/**
 * The screen root of a list screen: a full-height column the table fills.
 *
 * `className` is for a screen that has its own tuning to hang on that root and
 * nowhere else to hang it. The fill chain is written with the child combinator
 * (`.cib-page--fill > .cib-fill`), so a wrapper between this and the table card
 * silently stops the card filling anything - which is what the fleet list would
 * have needed for its eleven-column padding rule. One class on the root costs
 * nothing and keeps that chain intact.
 */
export function FillPage({
  children,
  className,
}: {
  readonly children: ReactNode;
  readonly className?: string;
}): JSX.Element {
  return <div className={cx('cib-page--fill', className)}>{children}</div>;
}
